// Google Analytics 4 utility functions
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// GA4 Measurement ID
export const GA_MEASUREMENT_ID = 'G-P8BK1G6JVH';

// Initialize Google Analytics
export const initGA = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
    });
  }
};

// Track page views
export const trackPageView = (page_path: string, page_title?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path,
      page_title: page_title || document.title,
    });
  }
};

// Track custom events
export const trackEvent = (event_name: string, parameters?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', event_name, parameters);
  }
};

// Track user registration
export const trackRegistration = (method: 'email' | 'google' | 'facebook', user_type: 'client' | 'freelancer') => {
  trackEvent('sign_up', {
    method,
    user_type,
  });
};

// Track user login
export const trackLogin = (method: 'email' | 'google' | 'facebook') => {
  trackEvent('login', {
    method,
  });
};

// Track project creation
export const trackProjectCreation = (project_type: string, budget_range: string) => {
  trackEvent('create_project', {
    project_type,
    budget_range,
  });
};

// Track service creation
export const trackServiceCreation = (service_category: string, price_range: string) => {
  trackEvent('create_service', {
    service_category,
    price_range,
  });
};

// Track project application
export const trackProjectApplication = (project_id: string, freelancer_id: string) => {
  trackEvent('apply_to_project', {
    project_id,
    freelancer_id,
  });
};

// Track service purchase
export const trackServicePurchase = (service_id: string, price: number, currency: string) => {
  trackEvent('purchase', {
    transaction_id: service_id,
    value: price,
    currency,
    items: [{
      item_id: service_id,
      item_name: 'Service',
      category: 'IT Service',
      quantity: 1,
      price: price,
    }],
  });
};

// Track credit purchase
export const trackCreditPurchase = (package_id: string, credits: number, price: number, currency: string) => {
  trackEvent('purchase', {
    transaction_id: package_id,
    value: price,
    currency,
    items: [{
      item_id: package_id,
      item_name: `${credits} Credits`,
      category: 'Credits',
      quantity: 1,
      price: price,
    }],
  });
};

// Track search events
export const trackSearch = (search_term: string, search_type: 'projects' | 'services' | 'freelancers') => {
  trackEvent('search', {
    search_term,
    search_type,
  });
};

// Track profile view
export const trackProfileView = (profile_id: string, profile_type: 'freelancer' | 'client') => {
  trackEvent('view_item', {
    item_id: profile_id,
    item_name: `${profile_type} profile`,
    item_category: 'Profile',
  });
};

// Track contact form submission
export const trackContactForm = (form_type: 'contact' | 'support' | 'feedback') => {
  trackEvent('contact_form_submit', {
    form_type,
  });
};

// Track file upload
export const trackFileUpload = (file_type: string, file_size: number) => {
  trackEvent('file_upload', {
    file_type,
    file_size,
  });
};

// Track promotion bid
export const trackPromotionBid = (promotion_id: string, bid_amount: number) => {
  trackEvent('promotion_bid', {
    promotion_id,
    bid_amount,
  });
};

// Track review submission
export const trackReviewSubmission = (review_type: 'project' | 'service', rating: number) => {
  trackEvent('review_submit', {
    review_type,
    rating,
  });
};

// Track admin actions
export const trackAdminAction = (action: string, target_type: string) => {
  trackEvent('admin_action', {
    action,
    target_type,
  });
};

// Track errors
export const trackError = (error_type: string, error_message: string, error_location: string) => {
  trackEvent('exception', {
    description: `${error_type}: ${error_message}`,
    fatal: false,
    error_location,
  });
};

// Track conversion events
export const trackConversion = (conversion_type: string, value?: number, currency?: string) => {
  trackEvent('conversion', {
    conversion_type,
    value,
    currency,
  });
};
